import { useState } from "react";
import { T, fontDisplay, fontMono } from "../theme";
import { detectRecurring, guessColumnMap, parseAmount, parseCsv, parseCsvDate } from "../engine/csv";
import { fmtDay, fmtMoney } from "../engine/dates";
import { loadImportedTransactions, saveImport, saveRecurring } from "../lib/data";

/**
 * CSV import.
 *
 * Every column is stored, not just the mapped ones. A bank export usually
 * carries something nobody thought to map, and the point of keeping the raw row
 * is that finding out later costs a query rather than a re-import.
 */
export default function ImportView({ onImported }) {
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [map, setMap] = useState({});
  const [kind, setKind] = useState("bank");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [candidates, setCandidates] = useState(null);
  const [chosen, setChosen] = useState({});

  const onFile = async (f) => {
    if (!f) return;
    setError(null);
    setResult(null);
    setCandidates(null);
    setFile(f);
    try {
      const text = await f.text();
      const p = parseCsv(text);
      if (!p.headers.length) throw new Error("No columns found — is this a CSV?");
      setParsed(p);
      setMap(guessColumnMap(p.headers));
    } catch (e) {
      setError(e.message || String(e));
    }
  };

  /**
   * Banks disagree on how to express direction. Some use one signed Amount,
   * others separate Paid in / Paid out columns. Both are handled, with the
   * split columns taking priority when present.
   */
  const toRow = (raw) => {
    const out = parseAmount(raw[map.paidOut]);
    const inn = parseAmount(raw[map.paidIn]);
    let amount = null;
    if (map.paidOut || map.paidIn) {
      if (out) amount = -Math.abs(out);
      else if (inn) amount = Math.abs(inn);
    }
    if (amount == null) amount = parseAmount(raw[map.amount]);
    return {
      txn_date: parseCsvDate(raw[map.date]),
      description: raw[map.description] || "",
      amount,
      balance: parseAmount(raw[map.balance]),
      raw,
    };
  };

  const preview = parsed ? parsed.rows.slice(0, 5).map(toRow) : [];
  const unmapped = parsed ? parsed.headers.filter((h) => !Object.values(map).includes(h)) : [];

  const doImport = async () => {
    setBusy(true);
    setError(null);
    try {
      const rows = parsed.rows.map(toRow).filter((r) => r.txn_date && r.amount != null);
      if (!rows.length) {
        throw new Error(
          "No rows had both a date and an amount — check the Date and Amount column mapping."
        );
      }
      await saveImport({
        filename: file.name,
        kind,
        headers: parsed.headers,
        columnMap: map,
        rows,
      });
      setResult({ imported: rows.length, skipped: parsed.rows.length - rows.length });

      // Detect across everything ever imported, not just this file: a quarterly
      // payment needs more history than one export usually holds.
      const all = await loadImportedTransactions(null);
      setCandidates(detectRecurring(all));
      onImported?.();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const addChosen = async () => {
    const rows = candidates
      .filter((c) => chosen[c.payee])
      .map((c) => ({
        payee: c.exampleDescription?.slice(0, 80) || c.payee,
        category: chosen[c.payee],
        amount: c.typicalAmount,
        is_variable: c.isVariable,
        frequency: c.frequency,
        day_of_month: c.dayOfMonth,
        payment_method: "cash",
        active: !c.stopped,
        source_note: `bank import · ${c.occurrences} payments · ${c.firstSeen} to ${c.lastSeen}`,
      }));
    if (!rows.length) return;
    setBusy(true);
    try {
      await saveRecurring(rows);
      setChosen({});
      setCandidates(candidates.filter((c) => !rows.some((r) => r.source_note.includes(c.firstSeen) && r.amount === c.typicalAmount)));
      onImported?.();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const field = (label, key) => (
    <label style={{ fontSize: 11, color: T.muted, display: "block" }}>
      {label}
      <br />
      <select
        value={map[key] || ""}
        onChange={(e) => setMap({ ...map, [key]: e.target.value })}
        style={{ fontSize: 12, padding: "4px 6px", minWidth: 160, marginTop: 2 }}
      >
        <option value="">— none —</option>
        {parsed.headers.map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div style={{ maxWidth: 1000 }}>
      <h2 style={{ fontFamily: fontDisplay, fontSize: 22, marginBottom: 4 }}>Import</h2>
      <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.6, maxWidth: 620 }}>
        Drop in a bank or card export. Every column is stored, not just the ones mapped below, so
        anything useful that wasn't anticipated is still there later. Recurring payments are
        proposed afterwards — nothing reaches the forecast until you say so.
      </p>

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          onFile(e.dataTransfer.files?.[0]);
        }}
        style={{
          border: `2px dashed ${T.border}`,
          borderRadius: 10,
          padding: 24,
          textAlign: "center",
          background: T.panelSoft,
          margin: "12px 0",
        }}
      >
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => onFile(e.target.files?.[0])}
          style={{ fontSize: 12 }}
        />
        <div style={{ fontSize: 11, color: T.muted, marginTop: 8 }}>or drop a .csv here</div>
      </div>

      {error && <div style={{ color: T.cost, fontSize: 12, marginBottom: 10 }}>{error}</div>}

      {parsed && !result && (
        <div style={{ background: T.panel, borderRadius: 10, padding: 16 }}>
          <div style={{ fontSize: 12, marginBottom: 10 }}>
            <strong>{file.name}</strong> · {parsed.rows.length} rows · {parsed.headers.length} columns
          </div>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
            <label style={{ fontSize: 11, color: T.muted, display: "block" }}>
              This is a
              <br />
              <select
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                style={{ fontSize: 12, padding: "4px 6px", marginTop: 2 }}
              >
                <option value="bank">bank account</option>
                <option value="amex">AMEX card</option>
                <option value="other">something else</option>
              </select>
            </label>
            {field("Date", "date")}
            {field("Description", "description")}
            {field("Amount (signed)", "amount")}
            {field("Paid out", "paidOut")}
            {field("Paid in", "paidIn")}
            {field("Balance", "balance")}
          </div>

          {unmapped.length > 0 && (
            <div style={{ fontSize: 11, color: T.muted, marginBottom: 10 }}>
              Kept but not mapped: {unmapped.join(", ")}
            </div>
          )}

          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ color: T.muted, textAlign: "left" }}>
                <th style={{ padding: 6 }}>Date</th>
                <th style={{ padding: 6 }}>Description</th>
                <th style={{ padding: 6, textAlign: "right" }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {preview.map((r, i) => (
                <tr key={i} style={{ borderTop: `1px solid ${T.borderSoft}` }}>
                  <td style={{ padding: 6, fontFamily: fontMono, color: r.txn_date ? T.ink : T.cost }}>
                    {r.txn_date || "unreadable"}
                  </td>
                  <td style={{ padding: 6 }}>{r.description || "—"}</td>
                  <td
                    style={{
                      padding: 6,
                      textAlign: "right",
                      fontFamily: fontMono,
                      color: r.amount == null ? T.cost : r.amount < 0 ? T.cost : T.income,
                    }}
                  >
                    {r.amount == null ? "unreadable" : fmtMoney(r.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <button
            onClick={doImport}
            disabled={busy}
            style={{
              marginTop: 14,
              padding: "7px 16px",
              borderRadius: 6,
              border: "none",
              background: T.accent,
              color: "#fff",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            {busy ? "Importing…" : `Import ${parsed.rows.length} rows`}
          </button>
        </div>
      )}

      {result && (
        <div style={{ background: T.panel, borderRadius: 10, padding: 16, marginTop: 12 }}>
          <div style={{ fontSize: 13, marginBottom: 4 }}>
            Imported {result.imported} rows
            {result.skipped ? ` · skipped ${result.skipped} without a readable date and amount` : ""}
          </div>

          {candidates && (
            <>
              <h3 style={{ fontFamily: fontDisplay, fontSize: 17, margin: "14px 0 4px" }}>
                Recurring payments found
              </h3>
              <p style={{ fontSize: 11, color: T.muted, marginBottom: 8 }}>
                Detected across every import, so quarterly and annual payments are caught too.
                Pick a category to add one to the forecast; leave it blank to ignore.
              </p>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                  <thead>
                    <tr style={{ color: T.muted, textAlign: "left" }}>
                      <th style={{ padding: 6 }}>Payee</th>
                      <th style={{ padding: 6, textAlign: "right" }}>Typical</th>
                      <th style={{ padding: 6 }}>Every</th>
                      <th style={{ padding: 6, textAlign: "right" }}>Per year</th>
                      <th style={{ padding: 6 }}>Seen</th>
                      <th style={{ padding: 6 }}>Add as</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.map((c) => (
                      <tr key={c.payee} style={{ borderTop: `1px solid ${T.borderSoft}` }}>
                        <td style={{ padding: 6 }}>
                          {c.exampleDescription}
                          {c.stopped && (
                            <span style={{ color: T.warn, marginLeft: 6 }}>· stopped?</span>
                          )}
                        </td>
                        <td style={{ padding: 6, textAlign: "right", fontFamily: fontMono }}>
                          {fmtMoney(c.typicalAmount)}
                          {c.isVariable && (
                            <div style={{ fontSize: 9, color: T.muted }}>
                              {fmtMoney(c.minAmount)}–{fmtMoney(c.maxAmount)}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: 6 }}>{c.frequency}</td>
                        <td style={{ padding: 6, textAlign: "right", fontFamily: fontMono }}>
                          {fmtMoney(c.annualValue)}
                        </td>
                        <td style={{ padding: 6, color: T.muted }}>
                          {c.occurrences}× · {fmtDay(c.lastSeen)}
                        </td>
                        <td style={{ padding: 6 }}>
                          <select
                            value={chosen[c.payee] || ""}
                            onChange={(e) => setChosen({ ...chosen, [c.payee]: e.target.value })}
                            style={{ fontSize: 11, padding: "2px 4px" }}
                          >
                            <option value="">— ignore —</option>
                            {["staff", "rent", "software", "media", "debt", "tax", "other"].map((k) => (
                              <option key={k} value={k}>
                                {k}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button
                onClick={addChosen}
                disabled={busy || !Object.values(chosen).some(Boolean)}
                style={{
                  marginTop: 12,
                  padding: "7px 16px",
                  borderRadius: 6,
                  border: "none",
                  background: Object.values(chosen).some(Boolean) ? T.accent : T.border,
                  color: Object.values(chosen).some(Boolean) ? "#fff" : T.muted,
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                Add selected to forecast
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
